import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  IconButton,
  Typography,
  withStyles,
} from '@material-ui/core';
import { ExpandLess, InfoOutlined, Search as SearchIcon } from '@material-ui/icons';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ExcavatorModal, TruckModal } from '~/components/EquipmentStatusModals';
import PF2MEquipmentPool from '~/components/PF2MEquipmentPool';
import PF2MTooltip from '~/components/PF2MTooltip';
import PF2MSearchInput from '~/components/PF2MSearchInput';
import {
  TruckAllocationModal,
  TruckAllocationModeModal,
  LoadAllocationModal,
} from '../../LoadSchedules/AllocationsTab/AllocationModal';
import OriginRow from './OriginRow';
import {
  getDispatchOriginsTrucks,
  getDispatchOriginsExcavators,
  updateTruckAllocations,
  setPendingTruckAllocationUpdate,
  setSelectedTruckAllocation,
  setSelectedLoadAllocation,
  getSyncStatus,
  getEquipmentStatuses,
  getEquipmentsSchedules,
  getEquipmentScheduleStatuses,
  getLoadAllocations,
} from '~/store/dispatch/actions';
import DispatchLabels from '../DispatchLabels';
import TruckOperators from '../TruckOperatorStatus';
import PF2MEquipmentScheduleModal from '~/components/PF2MEquipmentScheduleModal';
import PF2MFullScreenButton from '~/components/PF2MFullScreenButton';
import ContextMenu, { PF2MMenuItem, useContextMenu } from '~/components/PF2MMenuContext';
import { withModal } from '~/components/PF2MModal';
import PF2MWarnFeatureFlag from '~/PF2MWarnFeatureFlag';
import usePlatformConfiguration from '~/services/platformConfiguration';
import CollapseEquipmentList from '../CollapseEquipmentList';
import styles from '../styles';
import { DispatchDragNDropTypes, DispatchStatus, DispatchStatusNumber } from '~/utils';
import { newMomentDate } from '~/utils/moment';
import { getCodeFromOrigin, getCodeFromStatus, toCodesMapByKey } from '~/utils/codes';
import { getCodes, getOperators } from '~/store/manager/actions';
import ReloadQueue, { priority } from '../../ReloadQueue';
import useTabletConfiguration from '~/services/tabletConfiguration';

// const TRUCK = 1;
const LOAD = 2;

// No meaning to have validation for one line temporary toggle feature
// eslint-disable-next-line react/destructuring-assignment, react/prop-types
const OffComp = props => <div>{props.children}</div>;

const GENERIC_LOAD_EQUIP = {
  id: null,
  equip_type_id: 1,
  equip_group_id: 0,
  equip_id: 0,
  equip_name: '',
  priority: null,
  origin_element_id: null,
  origin_element_point_id: null,
  status: 'OP',
};

const EquipmentScheduleModal = withModal(PF2MEquipmentScheduleModal);

const OriginsTab = ({ classes }) => {
  const rootRef = React.useRef(0);

  const dispatch = useDispatch();
  const [currentTruckModal, setCurrentTruckModal] = useState(null);
  const [currentExcavatorModal, setCurrentExcavatorModal] = useState(null);
  const { t: translate } = useTranslation();
  const [truckContextMenuData, openTruckContextMenu, closeTruckContextMenu] = useContextMenu();
  const [loadContextMenuData, openLoadContextMenu, closeLoadContextMenu] = useContextMenu();
  const [truckAllocationModalData, setTruckAllocationModalData] = useState();
  const [loadAllocationModalData, setLoadAllocationModalData] = useState();
  const equipmentsSchedules = useSelector(state => state.dispatch.equipmentsSchedules);
  const [equipmentScheduleModal, setEquipmentScheduleModal] = useState(false);
  const processDisptachIndicatorsTime = (
    usePlatformConfiguration('process_dispatch_indicators_time')?.value || '0'
  );
  const [expanded, setExpanded] = useState(true);
  const handleFS = useFullScreenHandle();
  const [filterTextOrigin, setFilterTextOrigin] = useState('');
  const [filterTextLoad, setFilterTextLoad] = useState('');
  const [filterTextTruck, setFilterTextTruck] = useState('');
  const syncStatus = useSelector(state => state.dispatch.syncStatus);

  const { value: dispatchUpdateTime } = useTabletConfiguration('dispatch_update_time');

  const openTruckModal = (data) => {
    dispatch(getSyncStatus({ all: true },
      res => setCurrentTruckModal({
        ...data,
        ...res.find(item => item.equip_id === data.equip_id),
      })));
  };

  const openExcavatorModal = (data) => {
    dispatch(getSyncStatus({ all: true },
      res => setCurrentExcavatorModal({
        ...data,
        ...res.find(item => item.equip_id === data.equip_id),
      })));
  };

  const pendingTruckAllocationUpdate = useSelector(
    state => state.dispatch.pendingTruckAllocationUpdate,
  );
  const loadAllocations = useSelector(state => state.dispatch.loadAllocations);
  const excavatorRows = useSelector(
    state => [
      ...state.dispatch.excavatorOrigins
        .filter(r => r.priority !== null),
      ...state.dispatch.excavatorOrigins
        .filter(r => r.priority === null && r.status === DispatchStatus.OP),
    ],
  );
  const equipmentStatuses = useSelector(state => state.dispatch.equipmentStatuses);
  const codes = useSelector(state => state.manager.codes);
  const operators = useSelector(state => state.manager.operators);
  const codesByKey = useMemo(() => toCodesMapByKey(codes), [codes]);

  const durationsByExcavatorRows = excavatorRows.map((excavator) => {
    const matchingStatuses = equipmentStatuses.filter(
      status => status.id_equip === excavator.equip_id,
    );

    const durations = matchingStatuses.flatMap(status => status.duration);

    const mappedStatuses = matchingStatuses.map((status) => {
      const foundCode = getCodeFromStatus(codesByKey, status);
      const foundOperator = operators.find(op => op.id_operator === status.id_operator);
      const foundSyncStatus = syncStatus.find(s => s.equip_id === status.id_equip);

      return {
        ...status,
        codeId: foundCode ? foundCode.id : translate('validation:CodeNotFound'),
        codeName: foundCode ? foundCode.name : translate('validation:CodeNotFound'),
        operatorName: foundOperator ? foundOperator.name : translate('validation:OperatorNotFound'),
        timestampSyncRequest: foundSyncStatus ? foundSyncStatus.timestamp_sync_request : null,
        originalStatus: foundCode ? foundCode.code_type : null,
      };
    });

    const codesId = mappedStatuses.map(status => status.codeId);
    const codesNames = mappedStatuses.map(status => status.codeName);
    const operatorsNames = mappedStatuses.map(status => status.operatorName);
    const curStatus = mappedStatuses.map(status => status.originalStatus);
    const syncStatusAll = mappedStatuses.map(status => status.timestampSyncRequest);

    return {
      ...excavator,
      durations,
      codes_id: codesId,
      status: DispatchStatusNumber[curStatus],
      codes_name: codesNames,
      operators_name: operatorsNames,
      timestamp_sync_request: syncStatusAll,
    };
  });

  const truckOriginsEquipmentStatus = useSelector(state => (state.dispatch.truckOrigins || [])
    .filter(truck => truck?.equip_id)
    .map((truck) => {
      const truckStatus = equipmentStatuses.find(
        status => status.id_equip === truck.equip_id,
      );
      const code = getCodeFromStatus(codesByKey, truckStatus)
        ?? getCodeFromOrigin(codesByKey, truck);

      return {
        ...truck,
        code_id: truckStatus?.id_code || truck.code_id,
        status: DispatchStatusNumber[code?.code_type] || truck.status,
      };
    }));

  const excavatorOriginsEquipmentStatus = useSelector(state => (
    (state.dispatch.excavatorOrigins || [])
      .filter(excavator => excavator?.equip_id)
      .map((excavator) => {
        const excavatorStatus = equipmentStatuses.find(
          status => status.id_equip === excavator.equip_id,
        );
        const code = getCodeFromStatus(codesByKey, excavatorStatus)
          ?? getCodeFromOrigin(codesByKey, excavator);

        return {
          ...excavator,
          code_id: excavatorStatus?.id_code || excavator.code_id,
          status: DispatchStatusNumber[code?.code_type] || excavator.status,
        };
      })
  ));

  const availablePoolEquips = [
    ...truckOriginsEquipmentStatus
      .map(TruckOperators)
      .filter(st => st.isHealthy && !st.isOnCicle && !st.isOnQuickPause)
      .map(({ to }) => to),
    ...excavatorOriginsEquipmentStatus
      .filter(
        r => !r.priority
          && (r.status === DispatchStatus.PO || r.status === DispatchStatus.IN),
      ),
  ];

  const maintenancePoolEquips = [
    ...truckOriginsEquipmentStatus
      .map(TruckOperators)
      .filter(st => !st.isHealthy)
      .map(({ to }) => to),
    ...excavatorOriginsEquipmentStatus
      .filter(r => !r.priority && r.status === DispatchStatus.MA),
  ];
  const rightLaneTrucks = truckOriginsEquipmentStatus
    .map(TruckOperators)
    .filter(st => st.isHealthy)
    .filter(st => st.isOnCicle || st.isOnQuickPause)
    .filter(st => st.isLoading)
    .map(({ to }) => to);

  const leftLaneTrucks = truckOriginsEquipmentStatus
    .map(TruckOperators)
    .filter(st => st.isHealthy)
    .filter(st => st.isOnCicle || st.isOnQuickPause)
    .filter(st => !st.isLoading)
    .map(({ to }) => to);

  const reloadList = [
    { name: 'getDispatchOriginsTrucks', action: getDispatchOriginsTrucks },
    { name: 'getDispatchOriginsExcavators', action: getDispatchOriginsExcavators },
    { name: 'getEquipmentStatuses', action: getEquipmentStatuses },
    { name: 'getLoadAllocations', action: getLoadAllocations },
    { name: 'getCodes', action: getCodes },
    { name: 'getOperators', action: getOperators },
    { name: 'getEquipmentsSchedules', action: getEquipmentsSchedules },
    { name: 'getEquipmentScheduleStatuses', action: getEquipmentScheduleStatuses },
    { name: 'getSyncStatus', action: getSyncStatus },
  ];

  useEffect(() => {
    if (dispatchUpdateTime !== undefined) {
      ReloadQueue.setUpdateTime(Number(dispatchUpdateTime), dispatch);
    }

    ReloadQueue.clearReloadList(priority.MEDIUM, dispatch);
    ReloadQueue.addToReloadList(dispatch, reloadList, priority.MEDIUM);
    return () => {
      ReloadQueue.stopReload();
    };
  }, [dispatch, dispatchUpdateTime]);

  const equipScheduleModalData = useMemo(() => {
    if (!equipmentsSchedules || !equipmentScheduleModal) {
      return {};
    }
    const currentEquip = equipmentsSchedules
      .find(e => (e.group_equipament_links_id === equipmentScheduleModal.equip_id));
    return currentEquip || {
      ...equipmentScheduleModal,
      equipaments_id: equipmentScheduleModal.equip_type_id,
      equipament_groups_id: equipmentScheduleModal.equip_group_id,
      group_equipament_links_id: equipmentScheduleModal.equip_id,
    };
  }, [equipmentScheduleModal, equipmentsSchedules]);

  const allocateTruck = (_e, item) => {
    dispatch(setSelectedTruckAllocation(item));
    setTruckAllocationModalData(true);
  };

  const editTruckSchedule = (_e, item) => {
    setEquipmentScheduleModal(item);
  };

  const allocateLoader = (_e, item) => {
    dispatch(setSelectedLoadAllocation(item));
    setLoadAllocationModalData(LOAD);
  };

  const editLoadSchedule = (_e, item) => {
    setEquipmentScheduleModal(item);
  };

  const filteredLeftLaneTrucks = leftLaneTrucks
    .filter((truck) => {
      if (filterTextTruck !== '') {
        return (
          truck.equip_name
          && truck.equip_name !== null
          && truck.equip_name !== 0
          && truck.equip_name.toLowerCase().includes(filterTextTruck.toLowerCase())
        );
      }
      return (
        truck.current_origin_point_name
        && truck.current_origin_point_name !== null
        && truck.current_origin_point_name !== 0
        && truck.current_origin_point_name
          .toLowerCase()
          .includes(filterTextOrigin.toLowerCase())
      );
    })
    .map(truck => truck.current_excavator_name);

  const filteredRightLaneTrucks = rightLaneTrucks
    .filter((truck) => {
      if (filterTextTruck !== '') {
        return (
          truck.equip_name
          && truck.equip_name !== null
          && truck.equip_name !== 0
          && truck.equip_name.toLowerCase().includes(filterTextTruck.toLowerCase())
        );
      }
      return (
        truck.current_origin_point_name
        && truck.current_origin_point_name !== null
        && truck.current_origin_point_name !== 0
        && truck.current_origin_point_name
          .toLowerCase()
          .includes(filterTextOrigin.toLowerCase())
      );
    })
    .map(truck => truck.current_excavator_name);

  const uniqueIdsLeft = [
    ...new Set(filteredLeftLaneTrucks.filter(id => id !== null)),
  ];
  const uniqueIdsRight = [
    ...new Set(filteredRightLaneTrucks.filter(id => id !== null)),
  ];

  const filterExcavator = durationsByExcavatorRows.filter((excavator) => {
    if (filterTextLoad !== '') {
      return (
        excavator.equip_name
        && excavator.equip_name !== null
        && excavator.equip_name !== 0
        && excavator.equip_name
          .toLowerCase()
          .includes(filterTextLoad.toLowerCase())
      );
    }
    if (
      filterTextLoad === ''
      && filterTextTruck === ''
      && filterTextOrigin === ''
    ) {
      return true;
    }

    if (filterTextOrigin !== '') {
      const matchesOriginOrExcavator = excavator.current_origin_point_name?.toLowerCase()
        .includes(filterTextOrigin.toLowerCase())
        || uniqueIdsLeft.some(
          id => excavator.equip_name?.toLowerCase() === id.toLowerCase(),
        )
        || uniqueIdsRight.some(
          id => excavator.equip_name?.toLowerCase() === id.toLowerCase(),
        );

      return matchesOriginOrExcavator;
    }

    return (
      uniqueIdsLeft.some(
        id => excavator.equip_name?.toLowerCase() === id.toLowerCase(),
      )
      || uniqueIdsRight.some(
        id => excavator.equip_name?.toLowerCase() === id.toLowerCase(),
      )
    );
  });

  const renderFilterTable = () => (
    <div
      style={{
        display: 'flex',
        width: '100%',
        justifyContent: 'space-between',
        padding: '16px 8px',
        gap: '24px',
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: '45px',
        }}
      >
        <PF2MSearchInput
          value={filterTextOrigin}
          onChange={e => setFilterTextOrigin(e.target.value)}
          placeholder={translate('common:Find')}
          text={`${translate('common:SearchFor')} ${translate('common:Origin')}`}
          startIcon={<SearchIcon fontSize={'small'} color="primary" />}
        />
        <PF2MSearchInput
          value={filterTextLoad}
          onChange={e => setFilterTextLoad(e.target.value)}
          placeholder={translate('common:Find')}
          text={`${translate('common:SearchFor')} ${translate('common:LoadEquipment')}`}
          startIcon={<SearchIcon fontSize={'small'} color="primary" />}
        />
        <PF2MSearchInput
          value={filterTextTruck}
          onChange={e => setFilterTextTruck(e.target.value)}
          placeholder={translate('common:Find')}
          text={`${translate('common:SearchFor')} ${translate('common:TruckEquipment')}`}
          startIcon={<SearchIcon fontSize={'small'} color="primary" />}
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <PF2MTooltip
          title={<DispatchLabels />}
          PopperProps={{ disablePortal: true }}
        >
          <IconButton className={classes.iconButton}>
            <InfoOutlined fontSize="small" />
          </IconButton>
        </PF2MTooltip>
        <PF2MFullScreenButton
          isFullScreenActive={handleFS.active}
          open={handleFS.enter}
          close={handleFS.exit}
          variant="outlined"
        />
      </div>
    </div>
  );

  const renderTableHeader = () => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px' }}>
      <span className={classes.labelTypeSelector}>{translate('common:Element')}</span>
      <span className={classes.labelTypeSelector}>{translate('common:LoadEquipment')}</span>
    </div>
  );


  const renderGenericRow = () => {
    if (filterTextLoad !== '') return null;

    const workingExcavators = durationsByExcavatorRows.map(r => r.equip_id);

    const filteredLeftLane = leftLaneTrucks.filter((r) => {
      const isNotWorkingExcavator = !workingExcavators.includes(r.current_excavator_id);
      const matchesFilter = r.equip_name?.toLowerCase().includes(filterTextTruck?.toLowerCase());
      return isNotWorkingExcavator && matchesFilter;
    });

    const filterLeftOrigins = filteredLeftLane.filter((load) => {
      if (filterTextOrigin !== '') {
        return (
          load.current_origin_point_name?.toLowerCase()
            .includes(filterTextOrigin?.toLowerCase())
        );
      }
      return true;
    });

    const filteredRightLane = rightLaneTrucks.filter((r) => {
      const isNotWorkingExcavator = !workingExcavators.includes(r.current_excavator_id);
      const matchesFilter = r.equip_name?.toLowerCase().includes(filterTextTruck?.toLowerCase());
      return isNotWorkingExcavator && matchesFilter;
    });

    const filterRightOrigins = filteredRightLane.filter((load) => {
      if (filterTextOrigin !== '') {
        return (
          load.current_origin_point_name?.toLowerCase()
            .includes(filterTextOrigin?.toLowerCase())
        );
      }
      return true;
    });

    const leftLane = filterLeftOrigins.length > 0 || filterRightOrigins.length > 0
      ? leftLaneTrucks.filter(r => !workingExcavators.includes(r.current_excavator_id))
      : [];

    const rightLane = filterRightOrigins.length > 0 || filterLeftOrigins.length > 0
      ? rightLaneTrucks.filter(r => !workingExcavators.includes(r.current_excavator_id))
      : [];

    if (!leftLane.length && !rightLane.length) return null;

    return (
      <OriginRow
        origin={GENERIC_LOAD_EQUIP}
        leftLane={leftLane}
        rightLane={rightLane}
        isGenericRow
        loadAllocations={loadAllocations}
        openTruckModal={openTruckModal}
        openExcavatorModal={openExcavatorModal}
        onTruckContextMenu={openTruckContextMenu}
        onLoadContextMenu={openLoadContextMenu}
      />
    );
  };

  const renderRows = () => (
    <div style={{
      width: '100%',
      flex: 1,
      overflowY: 'scroll',
    }}
    >
      {renderFilterTable()}
      {renderTableHeader()}
      <div style={{
        borderTop: '1px solid rgba(224, 224, 224, 1)',
        borderBottom: '1px solid rgba(224, 224, 224, 1)',
      }}
      >
        <>
          {filterExcavator
            .map(r => (
              <OriginRow
                key={r.equip_id}
                origin={r}
                leftLane={leftLaneTrucks.filter(x => x.current_excavator_id === r.equip_id)}
                rightLane={rightLaneTrucks.filter(x => x.current_excavator_id === r.equip_id)}
                loadAllocations={loadAllocations}
                onTruckDrop={(item, allocationId) => {
                  if (!item.load_allocation_id) {
                    dispatch(updateTruckAllocations({ item, key: 'load_allocation_id', value: allocationId }));
                    return;
                  }

                  const newTruckAllocation = {
                    ...item,
                    load_allocation_id: allocationId,
                  };
                  dispatch(setPendingTruckAllocationUpdate(newTruckAllocation));
                }}
                openTruckModal={openTruckModal}
                openExcavatorModal={openExcavatorModal}
                onTruckContextMenu={openTruckContextMenu}
                onLoadContextMenu={openLoadContextMenu}
              />
            ))}
          {renderGenericRow()}
        </>
      </div>
    </div>
  );

  const renderPools = () => (
    <Accordion
      defaultExpanded
      onChange={() => setExpanded(!expanded)}
      expanded={expanded}
    >
      <AccordionSummary
        expandIcon={<ExpandLess />}
        id="1"
      >
        <Typography>{translate('common:AvailableEquipments')}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <div style={{ display: 'flex', width: '100%' }}>
          <div style={{
            display: 'flex',
            marginTop: 10,
            width: '100%',
          }}
          >
            <PF2MEquipmentPool
              equips={availablePoolEquips}
              loadAllocations={loadAllocations}
              onEquipClick={() => null}
              onItemDropped={() => null}
              headerText={translate('common:AvailableEquipments').toUpperCase()}
              truckItemType={DispatchDragNDropTypes.POOL_TRUCK}
              loadItemType={DispatchDragNDropTypes.POOL_LOAD}
              acceptDrop={false}
              onContextMenu={{
                truck: openTruckContextMenu,
                loader: openLoadContextMenu,
              }}
              operationTypeEffective
            />
            <PF2MEquipmentPool
              equips={maintenancePoolEquips}
              loadAllocations={loadAllocations}
              onEquipClick={() => null}
              onItemDropped={() => null}
              headerText={translate('common:MaintenanceEquipments').toUpperCase()}
              customStyles={{ marginLeft: '10px' }}
              truckItemType={DispatchDragNDropTypes.POOL_TRUCK}
              loadItemType={DispatchDragNDropTypes.POOL_LOAD}
              acceptDrop={false}
              onContextMenu={{
                truck: openTruckContextMenu,
                loader: openLoadContextMenu,
              }}
              operationTypeEffective
            />
          </div>
        </div>
      </AccordionDetails>
    </Accordion>
  );

  const children = (
    <div className={classes.tabContainer}>
      <FullScreen handle={handleFS}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: handleFS.active ? '95%' : '82vh',
            backgroundColor: '#ffffff',
          }}
        >
          {renderRows()}
          {renderPools()}
        </div>

        <div ref={rootRef}>
          <TruckModal
            modalData={currentTruckModal}
            closeModal={() => setCurrentTruckModal(null)}
            classes={classes}
            container={() => rootRef.current}
          />

          <ExcavatorModal
            modalData={currentExcavatorModal}
            closeModal={() => setCurrentExcavatorModal(null)}
            classes={classes}
            container={() => rootRef.current}
          />

          <TruckAllocationModeModal
            isOpen={!!pendingTruckAllocationUpdate}
            refreshOrigins
            close={() => dispatch(setPendingTruckAllocationUpdate(null))}
            container={() => rootRef.current}
          />

          <ContextMenu
            {...truckContextMenuData}
            close={closeTruckContextMenu}
            container={rootRef.current}
          >
            <PF2MMenuItem onClick={allocateTruck}>
              {translate('common:AddEditAllocation')}
            </PF2MMenuItem>
            <PF2MMenuItem onClick={editTruckSchedule}>
              {translate('common:EditEquipmentScheduling')}
            </PF2MMenuItem>
          </ContextMenu>

          <ContextMenu
            {...loadContextMenuData}
            close={closeLoadContextMenu}
            container={rootRef.current}
          >
            <PF2MMenuItem onClick={allocateLoader}>
              {translate('common:AddEditAllocation')}
            </PF2MMenuItem>
            <PF2MMenuItem onClick={editLoadSchedule}>
              {translate('common:EditEquipmentScheduling')}
            </PF2MMenuItem>
          </ContextMenu>

          <TruckAllocationModal
            modalData={truckAllocationModalData}
            closeModal={() => setTruckAllocationModalData(null)}
            container={() => rootRef.current}
          />

          <LoadAllocationModal
            isOpen={loadAllocationModalData}
            close={() => setLoadAllocationModalData(null)}
            container={() => rootRef.current}
          />

          {equipScheduleModalData && (
            <EquipmentScheduleModal
              container={() => rootRef.current}
              modalData={equipmentScheduleModal}
              closeModal={() => setEquipmentScheduleModal(false)}
              settings={{
                skipBulkUpdate: true,
              }}
              fixed={{
                equipmentType: equipScheduleModalData.equipaments_id || 0,
                equipmentGroup: equipScheduleModalData.equipament_groups_id || 0,
                equipment: equipScheduleModalData.group_equipament_links_id || 0,
                startTime: newMomentDate(),
                duration: '00:30:00',
                element: equipScheduleModalData.flowchart_element_id || 0,
                subElement: equipScheduleModalData.flowchart_element_point_id || 0,
              }}
            />
          )}
        </div>
      </FullScreen>
    </div>
  );
  return (
    <PF2MWarnFeatureFlag
      flag={processDisptachIndicatorsTime >= 1}
      featureName="DispatchIndicators (process_dispatch_indicators_time >= 1)"
      OffComponent={OffComp}
      OnComponent={CollapseEquipmentList}
      props={{
        classes: { container: classes.tabContainer },
        children,
      }}
    />
  );
};

OriginsTab.propTypes = {
  classes: PropTypes.object.isRequired,
};

OriginsTab.defaultProps = {};

export default withStyles(styles)(OriginsTab);
